import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { errorMessage } from '../errors';
import { collectionIcon } from '../shell/collections';
import { Alert, EmptyState, PageHeader, Pill } from '../ui';
import { ChevronRightIcon, SearchIcon } from '../ui/icons';
import { PageSearch } from './collection/CollectionToolbar';
import { ItemList, ResultsSkeleton } from './collection/ItemViews';
import { typesHaveCovers } from './collection/itemSummary';
import { collectionPersonal, isShared } from './item/personal';
import { MIN_QUERY, phpTrim, searchable } from '../shell/searchQuery';

const PER_COLLECTION = 5;

/** The fields an item of this collection is read through: its type's, else the default type's. */
const fieldsOfIn = (inventory) => (item) => {
  const types = (inventory && inventory.item_types) || [];
  const type = (item.item_type_id && types.find((candidate) => candidate.id === item.item_type_id))
    || (inventory && inventory.default_item_type) || types[0];
  return (type && type.fields) || (inventory && inventory.fields) || [];
};

/** One collection's matches: its name and count as a heading, up to five items, "See all N in …". */
function CollectionResults({ group, inventory, q }) {
  const { t, i18n } = useTranslation();
  const number = (n) => new Intl.NumberFormat(i18n.resolvedLanguage || 'en').format(n);
  const types = useMemo(() => (inventory && inventory.item_types) || [], [inventory]);
  const personal = useMemo(() => collectionPersonal(types), [types]);
  const Icon = collectionIcon(inventory || {});
  const headingId = `search-collection-${group.id}`;
  const query = new URLSearchParams([['q', q]]).toString();
  const listPath = `/c/${group.id}?${query}`;
  return (
    <section className="search-group" aria-labelledby={headingId}>
      <header className="search-group__header">
        <h2 id={headingId} className="search-group__title">
          <Link to={listPath}>
            <Icon className="search-group__icon" size={18} />
            <span>{group.name}</span>
          </Link>
          <span className="search-group__count">
            <span aria-hidden="true">{number(group.total)}</span>
            <span className="visually-hidden">{t('search.matches', { count: group.total })}</span>
          </span>
        </h2>
        {!group.is_mine && (
          <span className="search-group__owner">
            {t('shell.byOwner', { name: group.owner_name })}
            {' '}
            <Pill tone={group.permission === 'edit' ? 'accent' : 'amber'}>
              {t(group.permission === 'edit' ? 'shell.permEditLong' : 'shell.permReadLong')}
            </Pill>
          </span>
        )}
      </header>
      <ItemList
        items={group.items}
        fieldsOf={fieldsOfIn(inventory)}
        typeName={() => ''}
        icon={Icon}
        itemPath={(item) => `/c/${group.id}/items/${item.id}?${query}`}
        onOpen={() => {}}
        highlightId={null}
        showLocation={false}
        showCovers={typesHaveCovers(types)}
        personal={personal}
        shared={isShared(inventory)}
      />
      <p className="search-group__all">
        <Link to={listPath}>
          {t('search.seeAll', { count: group.total, name: group.name })}
          <ChevronRightIcon size={16} aria-hidden="true" />
        </Link>
      </p>
    </section>
  );
}

/**
 * /search?q= — every collection the reader owns or reads through a share,
 * searched at once (phase 3 §4.1): the collections whose name matches,
 * then one group per collection with its first items and a link to all of
 * them in the collection. The sidebar's box writes ?q= on a desktop; the
 * page has its own box below 1024 px (the phone's Search button opens it).
 */
function SearchPage({ inventories }) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const q = phpTrim(searchParams.get('q') || '');
  const ready = searchable(q);
  const [result, setResult] = useState(null);   // {q, collections, matching_collections, total}
  const [error, setError] = useState(null);
  const [fetching, setFetching] = useState(false);
  const counter = useRef(0);

  useEffect(() => {
    const request = ++counter.current;
    if (!ready) {
      setFetching(false);
      setResult(null);
      setError(null);
      return;
    }
    setFetching(true);
    api.get('/search', { params: { q, per_collection: PER_COLLECTION } })
      .then((response) => {
        if (request !== counter.current) return;
        setResult({ q, ...response.data });
        setError(null);
      })
      .catch((err) => { if (request === counter.current) setError(errorMessage(t, err)); })
      .finally(() => { if (request === counter.current) setFetching(false); });
  }, [q, ready]); // eslint-disable-line react-hooks/exhaustive-deps

  // Brought here by the Search button (phone, tablet): the page's box takes the focus once.
  useEffect(() => {
    if (!location.state || !location.state.focusSearch) return;
    const box = document.querySelector('[data-page-search]');
    if (box && box.offsetParent !== null) box.focus();
    navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: null });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const byId = useMemo(() => new Map((inventories || []).map((inv) => [inv.id, inv])), [inventories]);
  const search = (text) => {
    const next = phpTrim(text);
    if (next === q) return;
    setSearchParams(next ? { q: next } : {}, { replace: true });
  };

  const shown = result && result.q === q ? result : null;
  const collections = shown ? shown.collections : [];
  const matching = shown ? shown.matching_collections : [];
  const nothing = shown && collections.length === 0 && matching.length === 0;

  return (
    <div className="search-page">
      <PageHeader
        title={t('search.title')}
        subtitle={shown && !nothing
          ? t('search.summary', { count: shown.total })
          : null}
      />
      <PageSearch
        query={q}
        onSearch={search}
        label={t('shell.searchEverywhere')}
        placeholder={t('shell.searchEverywherePlaceholder')}
        className="search-page__box"
      />
      <span className="visually-hidden" aria-live="polite">
        {fetching ? t('collection.updating')
          : shown ? t('search.announce', { count: shown.total }) : ''}
      </span>

      {!ready && (
        <EmptyState icon={SearchIcon} title={t('search.startTitle')}>
          {q ? t('search.tooShort', { count: MIN_QUERY }) : t('search.start')}
        </EmptyState>
      )}
      {error && <Alert tone="danger">{t('search.error', { error })}</Alert>}
      {ready && !shown && !error && <ResultsSkeleton view="list" />}
      {nothing && (
        <EmptyState icon={SearchIcon} title={t('collection.noResults')}>
          {t('search.nothing', { query: q })}
        </EmptyState>
      )}

      {shown && (
        <div className={`search-results${fetching ? ' is-fetching' : ''}`} aria-busy={fetching}>
          {matching.length > 0 && (
            <section className="search-names" aria-labelledby="search-names-title">
              <h2 id="search-names-title" className="search-names__title">{t('search.collections', { count: matching.length })}</h2>
              <ul className="search-names__list">
                {matching.map((collection) => {
                  const Icon = collectionIcon(byId.get(collection.id) || {});
                  return (
                    <li key={collection.id}>
                      <Link to={`/c/${collection.id}`} className="search-names__link">
                        <Icon size={18} className="search-names__icon" />
                        <span className="search-names__name">{collection.name}</span>
                        {!collection.is_mine && <span className="search-names__owner">{t('shell.byOwner', { name: collection.owner_name })}</span>}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
          {collections.map((group) => (
            <CollectionResults key={group.id} group={group} inventory={byId.get(group.id)} q={q} />
          ))}
        </div>
      )}
    </div>
  );
}

export default SearchPage;
